import { IChartProps } from './Chart';
import { ILegendProps } from './Legend/Legend';
import { renderComponent } from 'utils/RenderingUtils';

export async function renderChart(container: HTMLElement, props: IChartProps, callback?: () => {}) {
  const { Chart } = await import('./Chart');
  const { isReportChartPart } = await import('./utils/ChartDataUtils');

  props.FolderId = Decisions.Navigation.currentFolderId;

  const ChartError = () => {
    return <div className="flex-centering full-size">{props.error}</div>;
  };

  if (isReportChartPart(props)) {
    // this is a report part, don't render it in PageHost:
    return renderComponent({
      container,
      component: props.error ? <ChartError /> : <Chart {...props} wrapperElement={container} />,
      callback,
      autoUnmount: true,
    });
  }

  props = await fillChartProps(container, props);

  return renderComponent({
    container,
    component: props.error ? <ChartError /> : <Chart {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderChartLegend(container: HTMLElement, props: ILegendProps, callback?: () => {}) {
  const { Chart } = await import('./Chart');
  const { Legend } = await import('./Legend/Legend');

  props.FolderId = Decisions.Navigation.currentFolderId;

  props = await fillChartProps(container, props);

  return renderComponent({
    container,
    component: <Chart {...props} render={renderProps => <Legend {...renderProps} />} />,
    autoUnmount: true,
    callback,
  });
}

async function fillChartProps(el: HTMLElement, props: IChartProps): Promise<IChartProps> {
  // keep related things also lazy-loaded:
  const { getInitialPageData, loadSessionPersonalization, mergePersonalization } = await import(
    'components/page/utils/PageDataUtils'
  );

  // previously wrapped chart in PageHost at this level for now, since here we are rendering data received from MVC.
  // but this caused sync problems across page, and too many API calls, so waiting on that.

  // aggregating props from local page cache and personalization:
  const pageProps = getInitialPageData(props.PageId);
  const { Configuration, CurrentData, Personalization, Error } = pageProps?.initialPartsData[props.ChartId] || {};
  let storedPersonalization = loadSessionPersonalization(props.PageId).Parts[props.ChartId];
  // why is precedence being given to props personalization here?
  // shouldn't it be given to personalization in local storage?
  // why shouldn't it be merged ahead of this?
  let personalization = mergePersonalization(storedPersonalization, Personalization || []).merged;

  return {
    ...props,
    currentData: CurrentData,
    configuration: Configuration,
    personalization,
    error: Error,
    wrapperElement: el,
  };
}
